import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { Account } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { ExchangeMark, fieldLabel } from "@/components/ui/kit";
import { ConnectPanel, ConnectResult, exchangeSubtitle } from "@/components/exchange/Connect";
import { useProviderInfos } from "@/hooks/useProviderInfos";
import {
  accountProviderTradeTypes,
  buildMarketplacePickerCards,
  isAccountProviderEnabled,
  sortAccountProviders,
  type MarketplacePickerCard,
} from "@/lib/account-provider";

const FALLBACK_PROVIDERS = ["binance", "mexc", "revolutx"];

/** "New account" dialog: pick an exchange, then follow the key guide. Same frame as New backtest. */
export default function NewAccountModal({ onClose }: { onClose: () => void }) {
  const api = useApi(["accounts"]);
  const providerInfos = useProviderInfos();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [providers, setProviders] = useState<string[]>(FALLBACK_PROVIDERS);
  const [selected, setSelected] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .commands.listConfigurables()
      .then((list: { account_providers?: string[] } | null) => {
        if (!cancelled && list?.account_providers?.length) setProviders(sortAccountProviders(list.account_providers));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.ready]);

  // Escape closes, unless focus is in a popover outside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const active = document.activeElement;
      if (active && active !== document.body && !dialogRef.current?.contains(active)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cards = useMemo(() => buildMarketplacePickerCards(providers), [providers]);
  const available = cards.filter((c) => isAccountProviderEnabled(c.provider));
  const typesFor = (card: MarketplacePickerCard) =>
    accountProviderTradeTypes(
      providerInfos?.account_providers.find((i) => i.id === card.provider),
      card.staticOptions
    );
  const current = available.find((c) => c.id === selected) || available[0];
  const created: Account | undefined = createdId ? (api.accounts || []).find((a: Account) => a.id === createdId) : undefined;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nacc-title"
        className="flex h-[min(760px,calc(100dvh-2rem))] w-full max-w-[760px] flex-col overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--bg-card)] shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[var(--border)] px-5 py-4">
          <h2 id="nacc-title" className="m-0 flex-1 text-base font-semibold text-[var(--text-primary)]">
            New account
          </h2>
          <button type="button" className="btn btn-ghost btn-sm" aria-label="Close" onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-[18px]">
          {created ? (
            <ConnectResult account={created} onDone={onClose} frame={false} />
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <span className={fieldLabel}>Exchange</span>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {available.map((card) => {
                    const on = current?.id === card.id;
                    return (
                      <button
                        key={card.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setSelected(card.id)}
                        className={`flex min-w-0 flex-col gap-1.5 rounded-[12px] border bg-[var(--bg-card-elevated)] p-2.5 text-left ${
                          on ? "border-[var(--primary)] shadow-[inset_0_0_0_1px_var(--primary)]" : "border-[var(--border)] hover:bg-[var(--bg-hover)]"
                        }`}
                      >
                        <span className="self-start">
                          <ExchangeMark provider={card.provider} size={24} />
                        </span>
                        <b className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{card.title}</b>
                        <span className="truncate text-xs text-[var(--text-muted)]">{exchangeSubtitle(card, typesFor(card))}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {current && (
                <div className="border-t border-[var(--divider)] pt-4">
                  <ConnectPanel key={current.id} card={current} types={typesFor(current)} frame={false} onConnected={setCreatedId} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
